import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { message } from 'antd';

import { queryClient } from 'config/query-client';
import { createSalesType, updateSalesType } from 'services/collections';

import useTranslation from 'customHooks/translate';
import { SalesType, SalesTypeError } from 'types';

import { Input } from './styled-components/common-components';
import CustomDialog from './CustomDialog';
import { SALES_TYPES } from 'events/types';

const defaultValues = {
  name: '',
};

const SalesTypeForm = ({ open, handleClose, salesTypeToUpdate, onSuccess }) => {
  const formRef = useRef<FormikProps<SalesType>>(null);
  const translation = useTranslation();

  const [nameError, setNameError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      title={translation.saleTypes.SaleType}
      buttonTitle={
        !!salesTypeToUpdate
          ? `${translation.taxTypes.Update}`
          : `${translation.taxTypes.Create}`
      }
      isLoading={loading}
      onSubmit={() => formRef.current?.handleSubmit()}
    >
      <Formik
        innerRef={formRef}
        initialValues={salesTypeToUpdate || defaultValues}
        onSubmit={(values, actions) => {
          setLoading(true);
          const method = salesTypeToUpdate ? updateSalesType : createSalesType;
          method({
            name: values.name,
            id: salesTypeToUpdate ? salesTypeToUpdate.id : undefined,
          })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: [SALES_TYPES],
              });
              handleClose();
              onSuccess(salesTypeToUpdate ? 'updated' : 'added');
            })
            .catch((error: SalesTypeError) => {
              error.response?.data?.Message &&
                message.error(error.response?.data?.Message);
              const errorMessageName = error.response?.data.errors
                ?.Name?.[0] as string | undefined;
              setNameError(errorMessageName || null);
            })
            .finally(() => {
              setLoading(false);
            });
        }}
        enableReinitialize
      >
        {(props) => (
          <Form>
            <div className="flex flex-col gap-[12px]">
              <label
                className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
                htmlFor="name"
              >
                {translation.saleTypes.SaleType} *
              </label>
              <Input
                className="d-block"
                type="text"
                name="name"
                onChange={props.handleChange}
                placeholder={translation.saleTypes.SaleType}
                value={props.values.name || ''}
                style={{
                  borderColor: nameError ? 'red' : '',
                }}
              />
              {nameError && (
                <span className="text-[12px] text-red-600">{nameError}</span>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </CustomDialog>
  );
};

export default SalesTypeForm;
